import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button, DateTimePicker } from '@/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getCompanyEmployees } from '../../api';
import { useEnterSubmit } from '@/common/hooks';

export const CRMTaskForm = forwardRef(function CRMTaskForm(
  {
    onSubmit,
    isPending,
    initialData,
    onCancel,
    companyId,
    variant = 'card',
    readOnly = false,
  },
  ref,
) {
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState(null);

  const [assignedToId, setAssignedToId] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const [debouncedAssigneeSearch, setDebouncedAssigneeSearch] = useState('');
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      if (initialData.due_at) {
        const d = new Date(initialData.due_at);
        setDueAt(isNaN(d.getTime()) ? null : d);
      } else {
        setDueAt(null);
      }
      const empId =
        initialData.assigned_to_employee_id || initialData.assigned_to_id;
      setAssignedToId(empId ? String(empId) : '');
      const empName =
        initialData.assigned_to_employee_name || initialData.assigned_to_name;
      setAssigneeName(empName || '');
      setAssigneeSearchQuery(empName || '');
    } else {
      setTitle('');
      setDueAt(null);
      setAssignedToId('');
      setAssigneeName('');
      setAssigneeSearchQuery('');
    }
  }, [initialData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAssigneeSearch(assigneeSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [assigneeSearchQuery]);

  const {
    data: assigneeData,
    isLoading: isAssigneesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['companyEmployees-search', companyId, debouncedAssigneeSearch],
    queryFn: ({ pageParam = 1 }) => {
      const params = debouncedAssigneeSearch
        ? { search: debouncedAssigneeSearch, page: pageParam }
        : { page: pageParam };
      return getCompanyEmployees(companyId, params);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next);
          return url.searchParams.get('page') || undefined;
        } catch {
          const match = lastPage.next.match(/[?&]page=(\d+)/);
          return match ? match[1] : undefined;
        }
      }
      return undefined;
    },
    enabled: !!companyId && isAssigneeDropdownOpen,
  });

  const assigneeList =
    assigneeData?.pages?.flatMap((page) =>
      Array.isArray(page) ? page : page?.results || [],
    ) || [];

  const handleAssigneeScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleSubmit = () => {
    if (!title.trim() || isPending) return;

    const payload = {
      title: title.trim(),
      due_at: dueAt ? dueAt.toISOString() : null,
      assigned_to_id: assignedToId ? parseInt(assignedToId, 10) : null,
      assigned_to_employee_id: assignedToId ? parseInt(assignedToId, 10) : null,
    };

    onSubmit(payload);

    if (!initialData) {
      setTitle('');
      setDueAt(null);
      setAssignedToId('');
      setAssigneeName('');
      setAssigneeSearchQuery('');
    }
  };

  const formRef = useEnterSubmit({
    onSubmit: handleSubmit,
    isPending,
  });

  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
    isValid: !!title.trim(),
  }));

  if (variant === 'inline') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text-secondary">
            Task Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter Title"
            className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors disabled:opacity-70"
            disabled={isPending || readOnly}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text-secondary">
            Due Date<span className="text-red-500">*</span>
          </label>
          <DateTimePicker
            selected={dueAt}
            onChange={(date) => setDueAt(date)}
            mode="datetime"
            allowPastDates={false}
            disablePastTime={true}
            placeholderText="Select due date & time"
            disabled={isPending || readOnly}
            className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors disabled:opacity-70"
          />
        </div>

        <div className="flex flex-col gap-2 relative">
          <label className="text-sm font-medium text-text-secondary">
            Assign User
          </label>
          <div className="relative flex items-center w-full">
            <input
              type="text"
              value={
                isAssigneeDropdownOpen ? assigneeSearchQuery : assigneeName
              }
              onChange={(e) => {
                if (readOnly) return;
                setAssigneeSearchQuery(e.target.value);
                setAssigneeName(e.target.value);
                setAssignedToId('');
                setIsAssigneeDropdownOpen(true);
              }}
              onFocus={() => {
                if (readOnly) return;
                setIsAssigneeDropdownOpen(true);
                setAssigneeSearchQuery(assigneeName);
              }}
              onBlur={() => {
                setTimeout(() => setIsAssigneeDropdownOpen(false), 200);
              }}
              placeholder="e.g Sahil Jani"
              className="w-full h-11 px-4 pr-10 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors disabled:opacity-70 cursor-text"
              disabled={isPending || readOnly}
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (readOnly || isPending) return;
                setIsAssigneeDropdownOpen((prev) => !prev);
              }}
              className="absolute right-0 top-0 bottom-0 px-3.5 flex items-center justify-center cursor-pointer text-[#868686] focus:outline-none z-10"
              aria-label="Toggle user dropdown"
              disabled={isPending || readOnly}
            >
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-transform ${isAssigneeDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>
            {isAssigneeDropdownOpen && (
              <div
                className="absolute z-[110] left-0 top-full w-full mt-1 bg-white border border-[#EBF4EA] rounded-xl shadow-2xl max-h-60 overflow-y-auto custom-scrollbar"
                onScroll={handleAssigneeScroll}
              >
                {isAssigneesLoading ? (
                  <div className="p-3 text-center text-xs text-[#868686]">
                    Loading...
                  </div>
                ) : assigneeList.length > 0 ? (
                  assigneeList.map((emp) => (
                    <div
                      key={emp.id}
                      className="px-4 py-2 hover:bg-[#F5F5F5] cursor-pointer transition-colors border-b border-[#EBF4EA] last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setAssignedToId(emp.id);
                        setAssigneeName(emp.name);
                        setAssigneeSearchQuery(emp.name);
                        setIsAssigneeDropdownOpen(false);
                      }}
                    >
                      <div className="text-sm font-medium text-[#1F2241]">
                        {emp.name}
                      </div>
                      <div className="text-xs text-[#868686]">{emp.email}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-center text-xs text-[#868686]">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-6 flex flex-col gap-5 p-5 rounded-2xl border border-border-default bg-input-background relative"
      ref={formRef}
    >
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-3 top-3 w-8 h-8 rounded-full bg-layer-2-input text-text-muted hover:text-text-primary flex items-center justify-center hover:bg-layer-3-border transition-all focus:outline-none z-10"
        >
          <svg
            className="w-4 h-4 stroke-[2.5]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
          Task Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs to be done?"
          className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none focus:border-primary transition-colors"
          disabled={isPending}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
            Due Date
          </label>
          <DateTimePicker
            selected={dueAt}
            onChange={(date) => setDueAt(date)}
            allowPastDates={false}
            allowFutureDates={true}
            disablePastTime={true}
            placeholderText="Select due date & time"
            disabled={isPending}
            className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        <div className="flex flex-col gap-2 relative">
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
            Assign User
          </label>
          <div className="relative flex items-center w-full">
            <input
              type="text"
              value={
                isAssigneeDropdownOpen ? assigneeSearchQuery : assigneeName
              }
              onChange={(e) => {
                setAssigneeSearchQuery(e.target.value);
                setAssigneeName(e.target.value);
                setAssignedToId(''); // clear ID when user types manually
                setIsAssigneeDropdownOpen(true);
              }}
              onFocus={() => {
                setIsAssigneeDropdownOpen(true);
                setAssigneeSearchQuery(assigneeName);
              }}
              onBlur={() => {
                setTimeout(() => setIsAssigneeDropdownOpen(false), 200);
              }}
              placeholder="Search and select user..."
              className="w-full h-11 px-4 pr-10 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none focus:border-primary transition-colors cursor-text"
              disabled={isPending}
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isPending) return;
                setIsAssigneeDropdownOpen((prev) => !prev);
              }}
              className="absolute right-0 top-0 bottom-0 px-3.5 flex items-center justify-center cursor-pointer text-text-muted focus:outline-none z-10"
              aria-label="Toggle user dropdown"
              disabled={isPending}
            >
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-transform ${isAssigneeDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>
            {isAssigneeDropdownOpen && (
              <div
                className="absolute z-[110] left-0 w-full mt-1 bg-layer-6-elevated border border-border-default rounded-xl shadow-2xl max-h-60 overflow-y-auto custom-scrollbar"
                onScroll={handleAssigneeScroll}
              >
                {isAssigneesLoading ? (
                  <div className="p-3 text-center text-xs text-text-muted">
                    Loading...
                  </div>
                ) : assigneeList.length > 0 ? (
                  assigneeList.map((emp) => (
                    <div
                      key={emp.id}
                      className="px-4 py-2 hover:bg-white/10 cursor-pointer transition-colors border-b border-border-default last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault(); // prevent blur
                        setAssignedToId(emp.id);
                        setAssigneeName(emp.name);
                        setAssigneeSearchQuery(emp.name);
                        setIsAssigneeDropdownOpen(false);
                      }}
                    >
                      <div className="text-sm font-medium text-text-primary">
                        {emp.name}
                      </div>
                      <div className="text-xs text-text-muted">{emp.email}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-center text-xs text-text-muted">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-2 pt-2 border-t border-border-default">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isPending || !title.trim()}
          variant="primary"
          className="h-10 px-6 text-sm font-semibold"
        >
          {isPending ? 'Saving...' : initialData ? 'Save Changes' : 'Add Task'}
        </Button>
      </div>
    </div>
  );
});
